import { ChevronRight, Flag, Plus, Trophy, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { ApiError } from '../../lib/api.ts';
import { useNotifications } from '../../lib/notifications.tsx';
import type {
    PuttingGameListItem, PuttingInvitation, PuttingStats,
} from '../../lib/putting.ts';
import {
    acceptGame, declineGame, formatAvg, getPuttingStats, listGames, listInvitations,
} from '../../lib/putting.ts';

/* L'accueil du putting.
 *
 * Trois choses, dans l'ordre où elles pressent : ce qu'on attend de vous (une
 * invitation), ce qui est en cours, puis ce que vous valez au putter.
 */

export function PuttingHome() {
    const navigate = useNavigate();
    // La cloche porte les notifications ; cet écran a besoin des invitations
    // elles-mêmes — nom de l'hôte, format, nombre de joueurs — pour pouvoir
    // répondre sans quitter la page. Deux besoins distincts, deux sources.
    const { refresh: refreshBell, notifications } = useNotifications();
    const [invitations, setInvitations] = useState<PuttingInvitation[]>([]);
    const [games, setGames] = useState<PuttingGameListItem[]>([]);
    const [stats, setStats] = useState<PuttingStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const reload = useCallback(async () => {
        try {
            const [g, s, i] = await Promise.all([
                listGames(), getPuttingStats(), listInvitations(),
            ]);
            setGames(g); setStats(s); setInvitations(i);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally {
            setLoading(false);
        }
    }, []);

    // Rechargé quand la cloche bouge sur une invitation.
    //
    // Cet écran n'a pas de relève à lui : sans ce lien, une invitation arrivée
    // pendant qu'on le regarde n'apparaîtrait qu'au prochain chargement de
    // page. Le compteur d'invitations non lues sert de signal — il ne change
    // que sur un fait qui concerne cet écran, là où l'ensemble des
    // notifications changerait à chaque relève.
    const inviteSignal = notifications
        .filter((n) => n.kind === 'putting_invite' && !n.readAt).length;

    useEffect(() => { void reload(); }, [reload, inviteSignal]);

    const accept = async (gameId: string): Promise<void> => {
        setBusy(gameId); setError(null);
        try {
            // Le putter se choisit sur l'écran de jeu : accepter doit rester un
            // seul geste. On répond « j'arrive » depuis un parking, on choisit
            // son putter en arrivant au green.
            await acceptGame(gameId);
            // La cloche aussi : l'invitation qu'on vient de trancher ne doit
            // pas continuer à y clignoter.
            await refreshBell();
            void navigate(`/putting/${gameId}`);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de rejoindre');
            setBusy(null);
        }
    };

    const decline = async (gameId: string): Promise<void> => {
        setBusy(gameId);
        try {
            await declineGame(gameId);
            // La cloche aussi : l'invitation qu'on vient de trancher ne doit
            // pas continuer à y clignoter.
            await refreshBell();
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Impossible de refuser');
        } finally {
            setBusy(null);
        }
    };

    const open = games.filter((g) => g.status === 'open' && g.myStatus === 'accepted');
    const past = games.filter((g) => g.status === 'finished');

    return (
        <div className="space-y-5 py-2">
            <header>
                <h1 className="text-2xl font-bold tracking-tight">Putting</h1>
                <p className="mt-1 text-sm opacity-60">
                    Chacun son tour, chacun sa carte. L'application compte les coups.
                </p>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* Ce qu'on attend de vous : en tête, toujours. */}
            {invitations.map((inv) => (
                <section key={inv.gameId} className="rounded-box border border-primary bg-base-200 p-4">
                    <div className="flex items-center gap-3">
                        <Avatar name={inv.hostName ?? '?'} avatarUrl={inv.hostAvatarUrl} size={40}
                            handicap={inv.hostHandicapIndex} />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm">
                                <span className="font-semibold">{inv.hostName ?? 'Un joueur'}</span>
                                {' '}vous invite à jouer
                            </p>
                            <p className="text-xs opacity-60">
                                {inv.name ? `${inv.name} · ` : ''}{inv.holeCount} trous ·{' '}
                                {inv.acceptedCount} joueur{inv.acceptedCount > 1 ? 's' : ''} déjà là
                            </p>
                        </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button type="button" disabled={busy === inv.gameId}
                            onClick={() => void decline(inv.gameId)}
                            className="btn btn-ghost flex-1">Refuser</button>
                        <button type="button" disabled={busy === inv.gameId}
                            onClick={() => void accept(inv.gameId)}
                            className="btn btn-primary flex-1 tap-target">Rejoindre</button>
                    </div>
                </section>
            ))}

            <Link to="/putting/nouvelle"
                className="btn btn-primary btn-lg h-20 w-full justify-start gap-4 text-left">
                <Plus size={28} />
                <span>
                    <span className="block text-lg font-bold">Nouvelle partie</span>
                    <span className="block text-sm font-normal opacity-80">
                        Seul ou avec vos partenaires
                    </span>
                </span>
            </Link>

            {loading && (
                <div className="flex justify-center py-8">
                    <span className="loading loading-ring loading-lg text-primary" />
                </div>
            )}

            {open.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-sm font-semibold">En cours</h2>
                    {open.map((g) => (
                        <Link key={g.id} to={`/putting/${g.id}`}
                            className="flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
                            <Flag size={22} className="shrink-0 text-primary" />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-semibold">
                                    {g.name ?? `Partie de ${g.hostName ?? 'putting'}`}
                                </span>
                                <span className="block text-xs opacity-60">
                                    {g.myHoles}/{g.holeCount} trous ·{' '}
                                    {g.playerCount} joueur{g.playerCount > 1 ? 's' : ''}
                                    {g.myLeftAt && ' · vous êtes sorti'}
                                </span>
                            </span>
                            <ChevronRight size={16} className="shrink-0 opacity-40" />
                        </Link>
                    ))}
                </section>
            )}

            {stats && stats.overall.holes > 0 && (
                <section className="rounded-box bg-base-200 p-4">
                    <h2 className="flex items-center gap-2 text-sm font-semibold">
                        <Trophy size={18} className="text-primary" /> Vos chiffres au putter
                    </h2>

                    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <div>
                            <div className="text-2xl font-bold tabular-nums text-primary">
                                {formatAvg(stats.overall.avgPutts)}
                            </div>
                            <div className="text-xs uppercase tracking-wide opacity-60">Coups/trou</div>
                        </div>
                        <div>
                            {/* La part de trous rentrés du premier coup : c'est
                                elle qui bouge quand le geste s'améliore, là où
                                la moyenne, coincée entre 1 et 3, remue à peine. */}
                            <div className="text-2xl font-bold tabular-nums">
                                {Math.round((stats.overall.onePutts * 100) / stats.overall.holes)}%
                            </div>
                            <div className="text-xs uppercase tracking-wide opacity-60">En 1 putt</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold tabular-nums">{stats.overall.holes}</div>
                            <div className="text-xs uppercase tracking-wide opacity-60">Trous</div>
                        </div>
                    </div>

                    {stats.clubs.length > 0 && (
                        <div className="mt-4 space-y-2">
                            {stats.clubs.map((c) => (
                                <div key={c.clubId} className="rounded-field bg-base-100 p-3">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="truncate text-sm font-semibold">
                                            {c.label}{c.brand ? ` · ${c.brand}` : ''}
                                        </span>
                                        <span className="shrink-0 font-bold tabular-nums">
                                            {formatAvg(c.avgPutts)}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-xs opacity-60">
                                        {c.holes} trous sur {c.games} partie{c.games > 1 ? 's' : ''} ·{' '}
                                        {c.onePutts} en 1 putt · {c.threePutts} à 3 coups ou plus
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {past.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-sm font-semibold">Parties terminées</h2>
                    {past.slice(0, 10).map((g) => (
                        <Link key={g.id} to={`/putting/${g.id}`}
                            className="flex items-center gap-3 rounded-box bg-base-200 p-3 transition-colors hover:bg-base-300">
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm">
                                    {g.name ?? `Partie de ${g.hostName ?? 'putting'}`}
                                </span>
                                <span className="block text-xs opacity-60">
                                    {new Date(g.startedAt).toLocaleDateString('fr-FR', {
                                        day: 'numeric', month: 'long',
                                    })}
                                    {' · '}{g.myHoles} trou{g.myHoles > 1 ? 's' : ''}
                                    {g.myPutts != null && ` · ${g.myPutts} coups`}
                                </span>
                            </span>
                            <ChevronRight size={16} className="shrink-0 opacity-40" />
                        </Link>
                    ))}
                </section>
            )}

            <Link to="/amis"
                className="flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
                <Users size={22} className="shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">Mes partenaires</span>
                    <span className="block text-xs opacity-60">
                        Ajouter quelqu'un par lien, ou retirer un partenaire
                    </span>
                </span>
                <ChevronRight size={16} className="shrink-0 opacity-40" />
            </Link>
        </div>
    );
}
